import {Observable, throwError as observableThrowError} from 'rxjs';
// core
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
// import 'rxjs/Rx';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

// services
import {ErrorMessageService} from '../alert-message/alert-message.service';

// models
import {catchError, map} from 'rxjs/operators';


/**
 * @description Decorator over angular HTTP for intercepting request and do custom manipulations
 */
@Injectable()
export class CustomHttp {

  constructor(private _http: HttpClient,
              private errorMessageService: ErrorMessageService,
              private _translateService: TranslateService) {
  }

  /**
   * @description GET method: Performs a request with `get` http method
   * @param url
   * @param options
   * @return {Observable<T>}
   */
  public get<T>(url: string, header?: HttpHeaders, params?: HttpParams): Observable<any> {
    const headers = this.requestOptions(header);
    const options = {
      headers: headers,
      params: params || new HttpParams(),
      observe: 'response' as const,
      responseType: 'text' as const,
    };
    return this._http.get(url, options).pipe(
      map(res => this.onSuccess(res)),
      catchError((error: HttpErrorResponse) => this.onCatch(error))
    );
  }

  /**
   * @description POST method: Performs a request with `post` http method
   * @param url
   * @param body
   * @param options
   * @return {Observable<T>}
   */
  public post<T>(url: string, body: any, header?: HttpHeaders, params?: HttpParams): Observable<any> {
    const headers = this.requestOptions(header);
    const options = {
      headers: headers,
      params: params || new HttpParams(),
      observe: 'response' as const,
      responseType: 'text' as const,
    };
    return this._http.post(url, body, options).pipe(
      catchError((error: any) => this.onCatch(error)),
      map(
        (res) => this.onSuccess(res),
        (error: any) => this.onError(error)
      )
    );
  }

  public patch<T>(url: string, body: any, header?: HttpHeaders, params?: HttpParams): Observable<any> {
    const headers = this.requestOptions(header);
    const options = {
      headers: headers,
      params: params || new HttpParams(),
      observe: 'response' as const,
      responseType: 'text' as const,
    };
    return this._http.patch(url, body, options).pipe(
      catchError((error: any) => this.onCatch(error)),
      map(
        (res) => this.onSuccess(res),
        (error: any) => this.onError(error)
      )
    );
  }

  /**
   * @description PUT method: Performs a request with `put` http method
   * @param url
   * @param body
   * @param options
   * @return {Observable<T>}
   */
  public put<T>(url: string, body: any, header?: HttpHeaders, params?: HttpParams): Observable<any> {
    const headers = this.requestOptions(header);
    const options = {
      headers: headers,
      params: params || new HttpParams(),
      observe: 'response' as const,
      responseType: 'text' as const,
    };
    return this._http.put(url, body, options).pipe(
      catchError((error: any) => this.onCatch(error)),
      map(
        (res) => this.onSuccess(res),
        (error: any) => this.onError(error)
      )
    );
  }

  /**
   * @description DELETE mthod: Performs a request with `delete` http method
   * @param url
   * @param options
   * @return {Observable<T>}
   */
  public delete<T>(url: string, header?: HttpHeaders, params?: HttpParams): Observable<any> {
    const headers = this.requestOptions(header);
    const options = {
      headers: headers,
      params: params || new HttpParams(),
      observe: 'response' as const,
      responseType: 'text' as const,
    };
    return this._http.delete(url, options).pipe(
      catchError((error: any) => this.onCatch(error)),
      map(
        (res) => this.onSuccess(res),
        (error: any) => this.onError(error)
      )
    );
  }

  /**
   * @description Request options
   * @param options
   * @returns {RequestOptionsArgs}
   */
  private requestOptions(header?: HttpHeaders): HttpHeaders {
    let headerObj;
    if (header == null || header.keys() == null || !header.keys().length) {
      headerObj = {
        'Accept-Language': this.getCurrentLanguage(),
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
    } else {
      headerObj = {
        'Accept-Language': this.getCurrentLanguage(),
      };
    }
    return new HttpHeaders(headerObj);
  }

  /**
   * @description Get the current language on the web page
   * @returns de/en
   */
  private getCurrentLanguage() {
    if (this._translateService.currentLang !== null) {
      return this._translateService.currentLang;
    }
    return 'de/en';
  }

  /**
   * @description Error handler
   * @param error
   * @returns {ErrorObservable}
   */
  private onCatch(error: HttpErrorResponse): Observable<any> {
    let response = error.error;
    try {
      response = JSON.parse(error.error);
    } catch (ex) {

    }
    return observableThrowError(response);
  }

  /**
   * @description If something needs to be done when the request was successful
   * @param res
   */
  private onSuccess(res: any): any {
    if (res.ok) {
      this._translateService.get('Created successfully').subscribe((res: string) => {
        this.errorMessageService.showSuccessToastr(res);
      });
    }
    let response;
    try {
      response = JSON.parse(res.body);
    } catch (ex) {
      response = res.body;
    }
    return response;
  }


  /**
   * @description Show a toastr when the request resolves with an error
   * @param error
   */
  private onError(error: any): void {
    if (typeof error.json === 'function') {

      if (error) {
        if (error.message) {
          this.errorMessageService.showErrorToastr(error.message);
        }
        if (error.length) {
          error.forEach((err) => {
            this.errorMessageService.showErrorToastr(err.message);
          });
        }
      }
    }
    return error.error;
  }

}
